import {
  CopyIcon,
  ExternalLinkIcon,
  MailIcon,
  UserIcon,
} from "lucide-react"
import { Fragment, useMemo } from "react"
import { useTranslation } from "react-i18next"
import { Link } from "react-router-dom"

import { Button } from "@/components/ui/button"
import { TableCell, TableRow } from "@/components/ui/table"
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { FRONTEND_ROUTES } from "@/constants"
import { SalesChannelEnum } from "@/enums"
import {
  formatDocument,
  formatMoney,
} from "@/helpers/format"
import { toast } from "@/hooks/use-toast"
import { cn } from "@/lib/utils"
import { orderResendMail } from "@/services/orders/order-resend-mail"
import type { Order } from "@/types/orders"
import { DateHelper } from "@/utils"

import { buttonVariants } from "../ui/button"
import type { OrderDate } from "./order-dates"
import { getOrderDates } from "./order-dates"
import { StatusTrackingSheet } from "./status-tracking-sheet"

interface OrdersTableRowProps {
  order: Order
}

export const OrdersTableRow = ({
  order,
}: OrdersTableRowProps) => {
  const { t } = useTranslation()

  const customerName = order.customer?.fullName || "-"
  const customerDocument = order.customer?.document
    ? formatDocument(order.customer.document)
    : "-"
  const customerEmail = order.customer?.email || "-"

  // Cada pedido mostra as datas que fazem sentido para ELE — ver `getOrderDates`.
  const dates = useMemo(
    () => getOrderDates(order),
    [order]
  )

  const handleCopy = (text: string, message: string) => {
    navigator.clipboard.writeText(text)
    toast({ title: message, variant: "success" })
  }

  const handleResendMail = async () => {
    const orderId = order.id ?? order.uuid ?? order.id
    const response = await orderResendMail(orderId)
    if (response.success) {
      toast({
        title: t("orders.emailResendSuccess"),
        variant: "success",
      })
    } else {
      toast({
        title: t("orders.emailResendError"),
        variant: "destructive",
      })
    }
  }

  const getSalesChannelValue = () => {
    if (
      typeof order.salesChannel === "object" &&
      order.salesChannel !== null
    ) {
      return order.salesChannel.status as SalesChannelEnum
    }
    return order.salesChannel as SalesChannelEnum
  }

  const canResendEmail =
    getSalesChannelValue() !==
    SalesChannelEnum.VTEX_PLATFORM

  return (
    <Fragment>
      <TableRow className="even:bg-zinc-50">
        {/* Data de Criação + Code + Status */}
        <TableCell className="align-top">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <div className="hidden items-center gap-1.5 md:flex">
                <span className="font-semibold">
                  {order.code}
                </span>
              </div>
              <div className="flex items-center gap-1 md:hidden">
                <span className="font-semibold">
                  {order.code}
                </span>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      onClick={() =>
                        handleCopy(
                          order.code,
                          t("orders.table.codeCopied")
                        )
                      }
                      className="rounded p-1 hover:bg-muted"
                    >
                      <CopyIcon
                        size={12}
                        className="text-muted-foreground"
                      />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent>
                    {t("common.copy")}
                  </TooltipContent>
                </Tooltip>
              </div>
              {/* Status badge mobile - ao lado do código */}
              {order?.status?.status && (
                <div className="md:hidden">
                  <StatusTrackingSheet
                    status={order.status.status}
                    description={order.status.description}
                    code={order.code}
                    events={order.statusTrackings}
                  />
                </div>
              )}
              {/* Copy button desktop */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    onClick={() =>
                      handleCopy(
                        order.code,
                        t("orders.table.codeCopied")
                      )
                    }
                    className="hidden rounded p-1 hover:bg-muted md:block"
                  >
                    <CopyIcon
                      size={12}
                      className="text-muted-foreground"
                    />
                  </button>
                </TooltipTrigger>
                <TooltipContent>
                  {t("common.copy")}
                </TooltipContent>
              </Tooltip>
            </div>
            {/* Status badge desktop - embaixo do código */}
            {order?.status && (
              <div className="hidden md:block">
                <StatusTrackingSheet
                  status={order.status.status}
                  description={order.status.description}
                  code={order.code}
                  events={order.statusTrackings}
                />
              </div>
            )}
            {/* A coluna de datas só aparece a partir de lg — abaixo disso este é o único
                lugar onde elas cabem. Nas duas, viravam a mesma informação lida duas vezes. */}
            <div className="text-xs text-muted-foreground lg:hidden">
              <OrderDatesList dates={dates} />
            </div>

            {/* Mobile: show all info */}
            <div className="mt-3 space-y-3 border-t pt-3 md:hidden">
              {/* IDs em grid */}
              <div className="grid grid-cols-2 gap-2">
                <div className="rounded-md bg-muted/50 p-2">
                  <p className="text-[10px] font-medium uppercase text-muted-foreground">
                    {t("orders.table.orderId")}
                  </p>
                  <p className="truncate font-mono text-xs">
                    {order.id || order.uuid}
                  </p>
                </div>
                {order.ownId && (
                  <div className="rounded-md bg-muted/50 p-2">
                    <p className="text-[10px] font-medium uppercase text-muted-foreground">
                      {t("orders.table.referenceCode")}
                    </p>
                    <p className="truncate font-mono text-xs">
                      {order.ownId}
                    </p>
                  </div>
                )}
              </div>

              {/* Cliente */}
              <div className="flex items-start gap-2 rounded-md border p-2">
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10">
                  <UserIcon
                    size={14}
                    className="text-primary"
                  />
                </div>
                <div className="min-w-0 flex-1 space-y-0.5">
                  <p className="truncate text-sm font-medium">
                    {customerName}
                  </p>
                  {order.customer?.document && (
                    <div className="flex items-center gap-1">
                      <p className="text-xs text-muted-foreground">
                        {customerDocument}
                      </p>
                      <button
                        type="button"
                        onClick={() =>
                          handleCopy(
                            order.customer.document,
                            t("orders.table.documentCopied")
                          )
                        }
                        className="rounded p-0.5 hover:bg-muted"
                      >
                        <CopyIcon
                          size={10}
                          className="text-muted-foreground"
                        />
                      </button>
                    </div>
                  )}
                  <p className="truncate text-xs text-muted-foreground">
                    {customerEmail}
                  </p>
                </div>
              </div>

              {/* Valor */}
              <div className="flex items-center justify-between rounded-md bg-muted/30 p-2">
                <div className="flex items-center gap-1.5">
                  {(order.discount ?? 0) > 0 && (
                    <span className="text-xs text-green-600">
                      -
                      {formatMoney(
                        (order.discount ?? 0) / 100
                      )}
                    </span>
                  )}
                </div>
                <span className="text-base font-bold text-primary">
                  {formatMoney(order.amount / 100)}
                </span>
              </div>

              {/* Ações */}
              <div className="flex items-center gap-2">
                <Link
                  to={`${FRONTEND_ROUTES.ORDERS.href}/details/${order.id || order.uuid}`}
                  className={cn(
                    buttonVariants({
                      size: "sm",
                      variant: "default",
                    }),
                    "flex-1"
                  )}
                >
                  <ExternalLinkIcon
                    size={14}
                    className="mr-1.5"
                  />
                  {t("common.viewDetails")}
                </Link>
                {canResendEmail && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleResendMail}
                  >
                    <MailIcon size={14} />
                  </Button>
                )}
              </div>
            </div>
          </div>
        </TableCell>

        {/* Identificação: UUID + OwnId */}
        <TableCell className="hidden align-top md:table-cell">
          <div className="space-y-2">
            <div>
              <p className="text-xs font-medium text-muted-foreground">
                {t("orders.table.orderId")}
              </p>
              <div className="flex items-center gap-1">
                <p
                  className="max-w-[200px] truncate text-sm"
                  title={order.id || order.uuid}
                >
                  {order.id || order.uuid}
                </p>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      onClick={() =>
                        handleCopy(
                          order.id,
                          t("orders.table.orderIdCopied")
                        )
                      }
                      className="rounded p-1 hover:bg-muted"
                    >
                      <CopyIcon
                        size={12}
                        className="text-muted-foreground"
                      />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent>
                    {t("common.copy")}
                  </TooltipContent>
                </Tooltip>
              </div>
            </div>
            {order.ownId && (
              <div>
                <p className="text-xs font-medium text-muted-foreground">
                  {t("orders.table.referenceCode")}
                </p>
                <div className="flex items-center gap-1">
                  <p
                    className="max-w-[200px] truncate text-sm"
                    title={order.ownId}
                  >
                    {order.ownId}
                  </p>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        type="button"
                        onClick={() =>
                          handleCopy(
                            order.ownId,
                            t(
                              "orders.table.referenceCodeCopied"
                            )
                          )
                        }
                        className="rounded p-1 hover:bg-muted"
                      >
                        <CopyIcon
                          size={12}
                          className="text-muted-foreground"
                        />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent>
                      {t("common.copy")}
                    </TooltipContent>
                  </Tooltip>
                </div>
              </div>
            )}
          </div>
        </TableCell>

        {/* Cliente */}
        <TableCell className="hidden align-top lg:table-cell">
          <div className="flex items-start gap-2">
            <Link
              to={`/customers/details/${order.customer?.id}`}
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10 transition-colors hover:bg-primary/20"
              title={t("orders.table.viewCustomer")}
            >
              <UserIcon
                size={14}
                className="text-primary"
              />
            </Link>
            <div className="min-w-0 flex-1 space-y-0.5">
              <p className="truncate font-medium">
                {customerName}
              </p>
              {order.customer?.document && (
                <div className="flex items-center gap-1">
                  <p className="text-xs text-muted-foreground">
                    {customerDocument}
                  </p>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        type="button"
                        onClick={() =>
                          handleCopy(
                            order.customer.document,
                            t("orders.table.documentCopied")
                          )
                        }
                        className="rounded p-0.5 hover:bg-muted"
                      >
                        <CopyIcon
                          size={10}
                          className="text-muted-foreground"
                        />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent>
                      {t("common.copy")}
                    </TooltipContent>
                  </Tooltip>
                </div>
              )}
              {order.customer?.email && (
                <div className="flex items-center gap-1">
                  <p
                    className="max-w-[160px] truncate text-xs text-muted-foreground"
                    title={order.customer.email}
                  >
                    {order.customer.email}
                  </p>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        type="button"
                        onClick={() =>
                          handleCopy(
                            order.customer.email,
                            t("orders.table.emailCopied")
                          )
                        }
                        className="rounded p-0.5 hover:bg-muted"
                      >
                        <CopyIcon
                          size={10}
                          className="text-muted-foreground"
                        />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent>
                      {t("common.copy")}
                    </TooltipContent>
                  </Tooltip>
                </div>
              )}
            </div>
          </div>
        </TableCell>

        <TableCell className="hidden align-top lg:table-cell">
          <span className="text-sm">
            {getSalesChannelValue()
              ? t(
                  `orders.salesChannels.${getSalesChannelValue()}` as const
                )
              : "-"}
          </span>
        </TableCell>

        <TableCell className="hidden align-top lg:table-cell">
          <span className="text-sm">
            {order.paymentMethod
              ? t(
                  `common.paymentMethods.${order.paymentMethod}` as const
                )
              : "-"}
          </span>
        </TableCell>

        {/* Desconto */}
        <TableCell className="hidden text-end align-top lg:table-cell">
          {(order.discount ?? 0) > 0 ? (
            <p className="text-sm font-medium text-green-600">
              -{formatMoney((order.discount ?? 0) / 100)}
            </p>
          ) : (
            <p className="text-sm text-muted-foreground">
              -
            </p>
          )}
        </TableCell>

        {/* Valor */}
        <TableCell className="hidden text-end align-top sm:table-cell">
          <p className="font-semibold">
            {formatMoney(order.amount / 100)}
          </p>
        </TableCell>

        {/* Data de Criação */}
        <TableCell className="hidden align-top lg:table-cell">
          <div className="text-sm text-muted-foreground">
            <OrderDatesList dates={dates} />
          </div>
        </TableCell>

        {/* Ações */}
        <TableCell className="hidden align-top md:table-cell">
          <div className="flex items-center justify-center gap-1">
            <Tooltip>
              <TooltipTrigger asChild>
                <Link
                  to={`${FRONTEND_ROUTES.ORDERS.href}/details/${order.id || order.uuid}`}
                  className={cn(
                    buttonVariants({
                      size: "icon",
                      variant: "ghost",
                    }),
                    "h-8 w-8"
                  )}
                >
                  <ExternalLinkIcon size={16} />
                  <span className="sr-only">
                    {t("common.viewDetails")}
                  </span>
                </Link>
              </TooltipTrigger>
              <TooltipContent>
                {t("common.viewDetails")}
              </TooltipContent>
            </Tooltip>

            {canResendEmail && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-8 w-8"
                    onClick={handleResendMail}
                  >
                    <MailIcon size={16} />
                    <span className="sr-only">
                      {t("orders.table.resendEmail")}
                    </span>
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  {t("orders.table.resendEmail")}
                </TooltipContent>
              </Tooltip>
            )}
          </div>
        </TableCell>
      </TableRow>
    </Fragment>
  )
}

interface OrderDatesListProps {
  dates: OrderDate[]
}

/**
 * As datas do pedido, uma por linha, com o rótulo antes do valor.
 *
 * <p>Sem o rótulo, três datas empilhadas viram três números sem significado — e é justamente
 * distinguir criação de pagamento que a coluna existe para fazer.</p>
 */
const OrderDatesList = ({
  dates,
}: OrderDatesListProps) => {
  const { t } = useTranslation()

  if (!dates.length) return <>-</>

  return (
    <>
      {dates.map((date) => (
        <p
          key={date.key}
          className="whitespace-nowrap tabular-nums"
        >
          <span className="mr-1 opacity-70">
            {t(`orders.table.dates.${date.key}`)}
          </span>
          {date.value}
        </p>
      ))}
    </>
  )
}
